'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Package {
  id: string;
  name: string;
  nameEN: string;
  duration: string;
  durationEN: string;
  price: number;
  priceLabel: string;
  priceLabelEN: string;
  description: string;
  descriptionEN: string;
  features: string[];
  featuresEN: string[];
  featured?: boolean;
  badge?: string;
  badgeEN?: string;
}

const packages: Package[] = [
  {
    id: 'pkg_first_exp',
    name: 'Prima Esperienza',
    nameEN: 'First Experience',
    duration: '1 ora',
    durationEN: '1 hour',
    price: 120,
    priceLabel: '€120 / ora',
    priceLabelEN: '€120 / hour',
    description: 'Perfetta per chi vuole scoprire l\'e-foil per la prima volta. Briefing completo incluso.',
    descriptionEN: 'Perfect for first-timers. Full safety briefing included, instructor guidance throughout.',
    features: ['Briefing sicurezza 15 min', 'Casco + giubbotto + impact vest', 'Istruttore in acqua', 'Assicurazione inclusa'],
    featuresEN: ['15-min safety briefing', 'Helmet + life jacket + impact vest', 'In-water instructor', 'Insurance included'],
    badge: 'Ideale per iniziare',
    badgeEN: 'Great to start',
  },
  {
    id: 'pkg_4h',
    name: '4 Ore',
    nameEN: '4 Hours',
    duration: '4 ore',
    durationEN: '4 hours',
    price: 380,
    priceLabel: '€380 / 4 ore',
    priceLabelEN: '€380 / 4 hours',
    description: 'La scelta più popolare. Più tempo per imparare, migliorare e vivere l\'esperienza con calma.',
    descriptionEN: 'Most popular choice. Explore the lake, improve your technique and enjoy the sunset.',
    features: [
      'Tutto incluso: attrezzatura, briefing e istruttore',
      'Pensato per 2 persone, con gestione flessibile',
      'Scegli tra esperienza in coppia nello stesso giorno o due sessioni in giorni diversi',
      'Date selezionabili direttamente in prenotazione',
      'Possibilità di aggiungere il video Insta360 dell\'esperienza',
    ],
    featuresEN: [
      'Everything included: equipment, briefing and instructor',
      'Designed for 2 people, with flexible management',
      'Choose between a couples session on the same day or two sessions on different days',
      'Dates selectable directly during booking',
      'Option to add the Insta360 video of your experience',
    ],
    featured: true,
    badge: '🔥 Più scelto',
    badgeEN: '🔥 Most popular',
  },
  {
    id: 'pkg_10h',
    name: '10 Ore',
    nameEN: '10 Hours',
    duration: '10 ore',
    durationEN: '10 hours',
    price: 800,
    priceLabel: '€800 / 10 ore',
    priceLabelEN: '€800 / 10 hours',
    description: 'Per chi vuole davvero padroneggiare l\'e-foil. Sessioni distribuite su più giorni.',
    descriptionEN: 'For those who want to truly master the e-foil. Sessions spread over multiple days.',
    features: ['Tutto incluso nel pacchetto 4h', 'Sessioni multi-giorno', 'Priorità slot'],
    featuresEN: ['Everything in 4h package', 'Multi-day sessions', '30% deposit available', 'Priority booking'],
    badge: 'Miglior valore',
    badgeEN: 'Best value',
  },
];

export default function PackagesPreview({ lang = 'it' }: { lang?: 'it' | 'en' }) {
  return (
    <section style={{ padding: 'var(--section-pad) 0' }}>
      <div className="container-main">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div className="badge badge-gold" style={{ marginBottom: '16px' }}>
            <Icon name="CurrencyEuroIcon" size={12} />
            {lang === 'it' ? 'Pacchetti & Prezzi' : 'Packages & Pricing'}
          </div>
          <h2
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 'clamp(2rem, 6vw, 3.5rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
              marginBottom: '16px',
            }}
          >
            {lang === 'it' ? (
              <>Scegli la tua <span style={{ color: '#00D4FF', fontStyle: 'italic' }}>esperienza</span></>
            ) : (
              <>Choose your <span style={{ color: '#00D4FF', fontStyle: 'italic' }}>experience</span></>
            )}
          </h2>
          <p style={{ color: 'rgba(240,247,255,0.55)', fontSize: '16px', maxWidth: '500px', margin: '0 auto' }}>
            {lang === 'it' ?'Tutti i pacchetti includono attrezzatura completa, briefing e assicurazione.' :'All packages include full equipment, briefing, and insurance.'}
          </p>
        </div>

        {/* Package cards */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          style={{
            gap: '20px',
            marginBottom: '32px',
          }}
        >
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`card-package${pkg.featured ? ' featured' : ''}`}
              style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}
            >
              {/* Featured ribbon */}
              {pkg.featured && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-1px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#00D4FF',
                    color: '#0B1F2E',
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    padding: '5px 16px',
                    borderRadius: '0 0 10px 10px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lang === 'it' ? '⭐ Più scelto' : '⭐ Most popular'}
                </div>
              )}

              <div style={{ padding: '28px 24px 20px', flex: 1 }}>
                {/* Badge */}
                {(pkg.badge || pkg.badgeEN) && (
                  <div className="badge badge-gold" style={{ marginBottom: '16px', marginTop: pkg.featured ? '12px' : '0' }}>
                    {lang === 'it' ? pkg.badge : pkg.badgeEN}
                  </div>
                )}

                {/* Name */}
                <h3
                  style={{
                    fontFamily: 'Fraunces, Georgia, serif',
                    fontSize: '24px',
                    fontWeight: 700,
                    marginBottom: '4px',
                  }}
                >
                  {lang === 'it' ? pkg.name : pkg.nameEN}
                </h3>

                {/* Price */}
                <div style={{ marginBottom: '16px' }}>
                  <span className="price-tag">
                    <span className="currency">€</span>
                    {pkg.price}
                  </span>
                  <span style={{ fontSize: '13px', color: 'rgba(240,247,255,0.4)', marginLeft: '6px' }}>
                    / {lang === 'it' ? pkg.duration : pkg.durationEN}
                  </span>
                </div>

                {/* Description */}
                <p style={{ fontSize: '14px', color: 'rgba(240,247,255,0.6)', lineHeight: 1.6, marginBottom: '20px' }}>
                  {lang === 'it' ? pkg.description : pkg.descriptionEN}
                </p>

                {/* Features */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(lang === 'it' ? pkg.features : pkg.featuresEN).map((f) => (
                    <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px' }}>
                      <Icon name="CheckCircleIcon" size={15} variant="solid" style={{ color: '#25D366', flexShrink: 0, marginTop: '1px' }} />
                      <span style={{ color: 'rgba(240,247,255,0.7)' }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div style={{ padding: '16px 24px 24px' }}>
                <Link
                  href={`/booking?pkg=${pkg.id}`}
                  className={pkg.featured ? 'btn-cyan' : 'btn-secondary'}
                  style={{ width: '100%', justifyContent: 'center', fontSize: '14px' }}
                >
                  <Icon name="CalendarDaysIcon" size={15} variant="solid" />
                  {lang === 'it' ? 'Prenota questo pacchetto' : 'Book this package'}
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* View all link */}
        <div style={{ textAlign: 'center' }}>
          <Link href="/experiences-pricing" className="btn-secondary" style={{ fontSize: '14px' }}>
            {lang === 'it' ? 'Vedi tutti i dettagli e le politiche' : 'View all details and policies'}
            <Icon name="ArrowRightIcon" size={15} />
          </Link>
        </div>
      </div>
    </section>
  );
}